/**
 * Legynd PDP interactions: gallery, variants, tabs, read-more, share.
 */

/**
 * @typedef {object} LegyndVariant
 * @property {number} id
 * @property {boolean} available
 * @property {number} price
 * @property {string} price_formatted
 * @property {number|null} compare_at_price
 * @property {string} sku
 * @property {string} title
 * @property {string|null} option1
 * @property {string|null} option2
 * @property {string|null} option3
 * @property {number|null} featured_media_id
 * @property {string[]} options
 * @property {number} [inventory_quantity]
 */

/**
 * @typedef {object} AddToCartLike
 * @property {() => void} [enable]
 * @property {() => void} [disable]
 */

class LegyndProduct extends HTMLElement {
  /** @type {LegyndVariant[]} */
  #variants = [];

  /** @type {string[]} */
  #optionNames = [];

  /** @type {Record<string, string>} */
  #selectedOptions = {};

  connectedCallback() {
    const dataId = this.dataset.productData;
    const dataEl = dataId ? document.getElementById(dataId) : null;
    if (dataEl) {
      try {
        const parsed = JSON.parse(dataEl.textContent || '{}');
        this.#variants = Array.isArray(parsed.variants) ? parsed.variants : [];
        this.#optionNames = Array.isArray(parsed.optionNames) ? parsed.optionNames : [];
      } catch {
        this.#variants = [];
        this.#optionNames = [];
      }
    }

    if (!this.#optionNames.length) {
      this.#optionNames = [1, 2, 3]
        .map((n) => this.getAttribute(`data-option-${n}-name`))
        .filter(/** @returns {name is string} */ (name) => typeof name === 'string' && name.length > 0);
    }

    this.querySelectorAll('[data-legynd-option][aria-checked="true"]').forEach((btn) => {
      const name = btn.getAttribute('data-option-name');
      const value = btn.getAttribute('data-option-value');
      if (name && value) this.#selectedOptions[name] = value;
    });

    // Seed selections from the currently selected variant id when options weren't marked selected.
    const variantInput = this.querySelector('[data-legynd-variant-id]');
    if (variantInput instanceof HTMLInputElement && variantInput.value) {
      const current = this.#variants.find((variant) => String(variant.id) === variantInput.value);
      if (current) this.#seedOptionsFromVariant(current);
    }

    this.#bindGallery();
    this.#bindVariants();
    this.#bindTabs();
    this.#bindReadMore();
    this.#bindShare();
    this.#applyVariant({ updateUrl: false });
  }

  /** @param {LegyndVariant} variant */
  #seedOptionsFromVariant(variant) {
    this.#optionNames.forEach((name, index) => {
      if (this.#selectedOptions[name]) return;
      const fromOptions = Array.isArray(variant.options) ? variant.options[index] : null;
      const fromKeyed = this.#optionValueAt(variant, index);
      const value = fromOptions ?? fromKeyed;
      if (value != null && value !== '') this.#selectedOptions[name] = String(value);
    });
  }

  /**
   * @param {LegyndVariant} variant
   * @param {number} index
   * @returns {string|null}
   */
  #optionValueAt(variant, index) {
    if (index === 0) return variant.option1;
    if (index === 1) return variant.option2;
    if (index === 2) return variant.option3;
    return null;
  }

  #bindGallery() {
    const thumbs = this.querySelectorAll('[data-legynd-thumb]');
    const main = this.querySelector('[data-legynd-main-media]');
    if (!(main instanceof HTMLImageElement) || !thumbs.length) return;

    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const src = thumb.getAttribute('data-media-src');
        const srcset = thumb.getAttribute('data-media-srcset');
        const alt = thumb.getAttribute('data-media-alt') || '';
        const mediaId = thumb.getAttribute('data-media-id');

        if (src) main.src = src;
        if (srcset) main.srcset = srcset;
        main.alt = alt;

        thumbs.forEach((t) => {
          t.classList.toggle('is-active', t === thumb);
          t.setAttribute('aria-current', t === thumb ? 'true' : 'false');
        });

        if (mediaId) this.dataset.activeMediaId = mediaId;
      });
    });
  }

  #bindVariants() {
    this.querySelectorAll('[data-legynd-option]').forEach((btn) => {
      btn.addEventListener('click', () => {
        if (btn instanceof HTMLButtonElement && btn.disabled) return;

        const name = btn.getAttribute('data-option-name');
        const value = btn.getAttribute('data-option-value');
        if (!name || !value) return;

        this.#selectedOptions[name] = value;

        this.querySelectorAll(`[data-legynd-option][data-option-name="${CSS.escape(name)}"]`).forEach((el) => {
          const selected = el === btn;
          el.classList.toggle('is-selected', selected);
          el.setAttribute('aria-checked', selected ? 'true' : 'false');
        });

        this.#applyVariant({ updateUrl: true });
      });
    });
  }

  /** @param {{updateUrl?: boolean}} [options] */
  #applyVariant(options = {}) {
    const { updateUrl = true } = options;
    if (!this.#variants.length) return;

    const variantInput = this.querySelector('[data-legynd-variant-id]');
    const selectedId =
      variantInput instanceof HTMLInputElement && variantInput.value ? variantInput.value : null;

    /** @type {LegyndVariant|null} */
    let match = null;
    if (this.#optionNames.length) {
      match =
        this.#variants.find((variant) =>
          this.#optionNames.every((name, index) => {
            const selected = this.#selectedOptions[name];
            if (selected == null) return false;
            const keyed = this.#optionValueAt(variant, index);
            const fromOptions = Array.isArray(variant.options) ? variant.options[index] : null;
            const candidate = keyed ?? fromOptions;
            return String(candidate) === String(selected);
          })
        ) || null;
    } else if (selectedId) {
      match = this.#variants.find((variant) => String(variant.id) === selectedId) || null;
    } else {
      match = this.#variants.find((variant) => this.#isVariantAvailable(variant)) || this.#variants[0] || null;
    }

    if (!match) return;

    if (variantInput instanceof HTMLInputElement) {
      variantInput.value = String(match.id);
    }

    const priceEl = this.querySelector('[data-legynd-price]');
    if (priceEl) {
      priceEl.textContent = match.price_formatted || this.#formatMoney(match.price);
    }

    const skuEl = this.querySelector('[data-legynd-sku]');
    if (skuEl instanceof HTMLElement) {
      skuEl.textContent = match.sku ? `SKU: ${match.sku}` : '';
      skuEl.hidden = !match.sku;
    }

    const isAvailable = this.#isVariantAvailable(match);
    const submit = this.querySelector('[data-legynd-add]');
    const addToCart = /** @type {AddToCartLike|null} */ (
      submit?.closest('add-to-cart-component') ?? null
    );

    if (submit instanceof HTMLButtonElement) {
      submit.disabled = !isAvailable;
      const label = submit.querySelector('[data-legynd-add-label]');
      if (label) {
        label.textContent = isAvailable
          ? this.dataset.addLabel || 'Add to Cart'
          : this.dataset.soldOutLabel || 'Sold out';
      }
    }

    if (isAvailable) addToCart?.enable?.();
    else addToCart?.disable?.();

    this.#updateOptionAvailability();

    if (match.featured_media_id) {
      const thumb = this.querySelector(`[data-legynd-thumb][data-media-id="${match.featured_media_id}"]`);
      if (thumb instanceof HTMLElement) thumb.click();
    }

    if (updateUrl) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', String(match.id));
      window.history.replaceState({}, '', url.toString());
    }
  }

  /** @param {LegyndVariant} variant */
  #isVariantAvailable(variant) {
    return variant.available === true;
  }

  #updateOptionAvailability() {
    this.#optionNames.forEach((name, optionIndex) => {
      this.querySelectorAll(`[data-legynd-option][data-option-name="${CSS.escape(name)}"]`).forEach((btn) => {
        const value = btn.getAttribute('data-option-value');
        if (value == null) return;

        const hasAvailableVariant = this.#variants.some((variant) => {
          if (!this.#isVariantAvailable(variant)) return false;
          return this.#optionNames.every((otherName, otherIndex) => {
            const keyed = this.#optionValueAt(variant, otherIndex);
            const fromOptions = Array.isArray(variant.options) ? variant.options[otherIndex] : null;
            const candidate = keyed ?? fromOptions;
            if (otherIndex === optionIndex) return String(candidate) === String(value);
            const selected = this.#selectedOptions[otherName];
            if (selected == null) return true;
            return String(candidate) === String(selected);
          });
        });

        if (btn instanceof HTMLButtonElement) {
          btn.disabled = !hasAvailableVariant;
        }
      });
    });
  }

  /** @param {number} cents */
  #formatMoney(cents) {
    const template = this.dataset.moneyFormat || '${{amount}}';
    const amount = (cents / 100).toFixed(2);
    const amountNoDecimals = String(Math.round(cents / 100));
    return template
      .replace('{{amount_no_decimals}}', amountNoDecimals)
      .replace('{{amount}}', amount)
      .replace('{{amount_with_comma_separator}}', amount.replace('.', ','));
  }

  #bindTabs() {
    const tabs = this.querySelectorAll('[data-legynd-tab]');
    const panels = this.querySelectorAll('[data-legynd-panel]');
    if (!tabs.length) return;

    tabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        const target = tab.getAttribute('data-legynd-tab');
        tabs.forEach((t) => {
          const active = t === tab;
          t.classList.toggle('is-active', active);
          t.setAttribute('aria-selected', active ? 'true' : 'false');
        });
        panels.forEach((panel) => {
          if (!(panel instanceof HTMLElement)) return;
          panel.hidden = panel.getAttribute('data-legynd-panel') !== target;
        });
      });
    });
  }

  #bindReadMore() {
    const toggle = this.querySelector('[data-legynd-read-more]');
    const body = this.querySelector('[data-legynd-desc-body]');
    if (!(toggle instanceof HTMLElement) || !(body instanceof HTMLElement)) return;

    if (body.scrollHeight <= body.clientHeight + 8) {
      toggle.hidden = true;
      body.classList.add('is-expanded');
      return;
    }

    toggle.addEventListener('click', () => {
      const expanded = body.classList.toggle('is-expanded');
      toggle.textContent = expanded
        ? this.dataset.readLessLabel || 'Read Less'
        : this.dataset.readMoreLabel || 'Read More...';
      toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    });
  }

  #bindShare() {
    const url = () => window.location.href;
    const title = this.dataset.productTitle || document.title;

    this.querySelector('[data-share="copy"]')?.addEventListener('click', async () => {
      try {
        await navigator.clipboard.writeText(url());
        const btn = this.querySelector('[data-share="copy"]');
        if (btn instanceof HTMLElement) {
          const prev = btn.getAttribute('aria-label') || 'Copy link';
          btn.setAttribute('aria-label', this.dataset.copiedLabel || 'Link copied');
          window.setTimeout(() => btn.setAttribute('aria-label', prev), 2000);
        }
      } catch {
        /* noop */
      }
    });

    this.querySelector('[data-share="x"]')?.addEventListener('click', () => {
      window.open(
        `https://twitter.com/intent/tweet?url=${encodeURIComponent(url())}&text=${encodeURIComponent(title)}`,
        '_blank',
        'noopener,noreferrer'
      );
    });

    this.querySelector('[data-share="facebook"]')?.addEventListener('click', () => {
      window.open(
        `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url())}`,
        '_blank',
        'noopener,noreferrer'
      );
    });

    this.querySelector('[data-share="email"]')?.addEventListener('click', () => {
      window.location.href = `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(url())}`;
    });
  }
}

if (!customElements.get('legynd-product')) {
  customElements.define('legynd-product', LegyndProduct);
}
